import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { ASSETS } from "../lib/assets";
import { Overline, Reveal } from "../components/Section";

export default function Products() {
  return (
    <div data-testid="products-page" className="bg-slate-950 text-white">
      <section className="relative pt-40 pb-20 overflow-hidden grain">
        <div className="absolute inset-0">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[600px] rounded-full bg-teal-500/15 blur-[120px]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal><Overline>Our products</Overline></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.98]">
              Three worlds. <span className="text-gradient-teal">One vision.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl mx-auto text-lg text-slate-300 leading-relaxed">
              Each PJSRS product is built around a single belief — that intelligent technology should
              make life better, lift talent, and connect communities.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-8">
          {/* Fit2Fin */}
          <Reveal>
            <Link
              to="/fit2fin"
              data-testid="products-card-fit2fin"
              className="group relative grid grid-cols-1 lg:grid-cols-12 gap-10 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-sky-500/10 p-8 md:p-12 hover:-translate-y-1 transition-transform"
            >
              <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-emerald-500/20 blur-[110px]" />
              <div className="relative lg:col-span-5 flex flex-col gap-6">
                <div className="flex items-center gap-4">
                  <img src={ASSETS.logos.fit2fin} alt="Fit2Fin" className="h-20 w-20 rounded-2xl shadow-xl ring-1 ring-white/10" />
                  <div>
                    <Overline color="text-emerald-300">India's First Habit Platform</Overline>
                    <div className="font-display text-3xl font-black mt-1">Fit2Fin<sup className="text-xs font-bold align-super text-slate-400">™</sup></div>
                  </div>
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-[1.02]">
                  Every <span className="text-gradient-fit">Healthy Habit</span><br /> Deserves a <span className="text-gradient-fit">Reward</span>
                </h2>
                <p className="text-slate-300 max-w-md">
                  India's AI-powered habit & wellness rewards platform. Build healthy habits, earn Fit Points,
                  and unlock everyday benefits through the Fit2Fin Rewards Ecosystem.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Habit Building", "Fit Points", "AI Personalization", "Rewards Marketplace"].map((t) => (
                    <span key={t} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs">{t}</span>
                  ))}
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-emerald-300 font-medium">
                  Explore Fit2Fin <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
              <div className="relative lg:col-span-7">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-white/10">
                  <img src={ASSETS.images.fit2finHero} alt="Fit2Fin" className="h-full w-full object-cover" />
                </div>
              </div>
            </Link>
          </Reveal>

          {/* CricStoryLive */}
          <Reveal delay={0.1}>
            <Link
              to="/cricstorylive"
              data-testid="products-card-cricstory"
              className="group relative grid grid-cols-1 lg:grid-cols-12 gap-10 overflow-hidden rounded-3xl border border-yellow-500/20 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black p-8 md:p-12 hover:-translate-y-1 transition-transform"
            >
              <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-yellow-500/15 blur-[110px]" />
              <div className="relative lg:col-span-5 flex flex-col gap-6">
                <div className="flex items-center gap-4">
                  <img src={ASSETS.logos.cricstorylive} alt="CricStoryLive" className="h-20 w-20 rounded-2xl ring-1 ring-yellow-500/20 bg-black object-contain" />
                  <div>
                    <Overline color="text-yellow-400">AI-Powered Digital Cricket Ecosystem</Overline>
                    <div className="font-display text-3xl font-black mt-1 text-white">CricStoryLive</div>
                  </div>
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-[1.02]">
                  Every Run<br />
                  <span className="text-gradient-gold">Tells a Story.</span>
                </h2>
                <p className="text-slate-300 max-w-md">
                  An AI-powered digital cricket ecosystem that celebrates every player, every match, and
                  every story — from local playgrounds to international stadiums.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Live Cricket", "Player Stories", "Grassroots Talent", "AI Match Intelligence"].map((t) => (
                    <span key={t} className="rounded-full bg-yellow-500/10 border border-yellow-500/20 px-3 py-1.5 text-xs text-yellow-200">{t}</span>
                  ))}
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-yellow-400 font-medium">
                  Explore CricStoryLive <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
              <div className="relative lg:col-span-7">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-yellow-500/20">
                  <img src={ASSETS.images.cricstoryHero} alt="CricStoryLive" className="h-full w-full object-cover" />
                </div>
              </div>
            </Link>
          </Reveal>

          {/* CrazyCricket — Coming Soon (announcement card only, no product page) */}
          <Reveal delay={0.2}>
            <div
              data-testid="products-card-crazycricket"
              className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 overflow-hidden rounded-3xl border border-lime-500/20 bg-gradient-to-br from-lime-500/10 via-slate-900 to-emerald-500/10 p-8 md:p-12"
            >
              <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-lime-500/15 blur-[110px]" />
              <div className="relative lg:col-span-5 flex flex-col gap-6 justify-center">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-2xl bg-white ring-1 ring-white/10 shadow-xl overflow-hidden grid place-items-center">
                    <img src={ASSETS.logos.crazycricket} alt="CrazyCricket" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <Overline color="text-lime-300">A Cricket IQ Platform</Overline>
                    <div className="font-display text-3xl font-black mt-1 text-white">CrazyCricket</div>
                  </div>
                </div>
                <p className="text-slate-300 max-w-md">Product details coming soon.</p>
                <div className="mt-2">
                  <span
                    data-testid="products-crazycricket-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-lime-500/30 px-5 py-2.5 text-sm font-medium text-lime-300 cursor-default select-none"
                  >
                    <Lock className="h-4 w-4" /> Details Coming Soon
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400" />
                    </span>
                  </span>
                </div>
              </div>
              <div className="relative lg:col-span-7">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-lime-500/20 bg-white grid place-items-center">
                  <img src={ASSETS.logos.crazycricket} alt="CrazyCricket" className="h-full w-full object-contain p-8" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* SalaryAI — hidden until launch. Uncomment when ready:
          <Reveal delay={0.2}>
            <Link to="/salaryai" data-testid="products-card-salaryai">SalaryAI card — see git history commit 98d22eb for full markup</Link>
          </Reveal>
          */}
        </div>
      </section>
    </div>
  );
}
